import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PaymentService } from '../../../core/services/payment.service';
import { PaymentGatewayData } from '../../../core/models/payment/payment-gateway.interface';
import { PaymentResult, PaymentStatus } from '../../../core/models/payment/payment.model';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { CheckoutSingleComponent } from '../checkout/checkout-single/checkout-single.component';
import { BaseComponent } from '../../../core/shared/base.component';
import { AlignetPaymentGatewayService } from '../../../core/services/alignet-payment-gateway.service';


@Component({
  selector: 'app-payment',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    NgxSpinnerModule,
    CheckoutSingleComponent
  ],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss'
})
export class PaymentComponent extends BaseComponent implements OnInit {
  @ViewChild('alignetForm', { static: false }) alignetForm!: ElementRef;
  @ViewChild('alignetLoading', { static: false }) alignetLoading!: ElementRef;

  planId: string = '';
  planName: string = '';
  amount: number = 0;
  currency: string = 'PEN';
  frequency: string = 'anual';
  paymentResult: PaymentResult | null = null;

  // Valores del formulario
  cardName: string = '';
  cardNumber: string = '';
  expiryDate: string = '';
  cvv: string = '';
  saveCardData: boolean = false;
  acceptTerms: boolean = false;

  // Estado del método de pago seleccionado
  selectedPaymentMethod: 'VISA' | 'Mastercard' | 'Paypal' | 'ALIGNET' = 'VISA';
  
  // Estado para controlar qué formulario mostrar
  showAlignetForm: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private paymentService: PaymentService,
    private spinner: NgxSpinnerService,
    private alignetPaymentService: AlignetPaymentGatewayService
  ) {
    super();
  }

  ngOnInit(): void {
    // Obtenemos los parámetros de la URL
    this.route.queryParams.subscribe(params => {
      this.planId = params['planId'] || '';
      this.planName = params['planName'] || '';
      this.amount = parseFloat(params['amount']) || 0;
      this.currency = params['currency'] || 'PEN';
      this.frequency = params['frequency'] || 'anual';

      console.log('params', params);

      // Si no hay un plan seleccionado, redirigimos a la selección de planes
      if (!this.planId || !this.planName || !this.amount) {
        this.router.navigate(['/dashboard/plans']);
      }
    });
  }

  setPaymentMethod(method: 'VISA' | 'Mastercard' | 'Paypal' | 'ALIGNET'): void {
    this.selectedPaymentMethod = method;
    
    // Si se selecciona Alignet, cargar el formulario
    if (method === 'ALIGNET') {
      this.showAlignetForm = true;
      this.cargarFormularioAlignet();
    } else {
      this.showAlignetForm = false;
    }
  }

  handleSubmit(): void {
    if (!this.acceptTerms) {
      // alert('Debes aceptar los términos y condiciones para continuar.');
      this.info('Debes aceptar los términos y condiciones para continuar.', '', 'Aceptar', true);
      return;
    }

    if (!this.validateForm()) {
      // alert('Por favor, completa todos los campos requeridos del formulario.');
      this.info('Por favor, completa todos los campos requeridos del formulario.', '', 'Aceptar', true);
      return;
    }

    this.spinner.show();

    const paymentData: PaymentGatewayData = {
      planId: this.planId,
      amount: this.amount,
      currency: this.currency,
      paymentMethod: this.selectedPaymentMethod,
      cardDetails: {
        cardName: this.cardName,
        cardNumber: this.cardNumber.replace(/\s/g, ''),
        expiryDate: this.expiryDate,
        cvv: this.cvv
      },
      savePaymentMethod: this.saveCardData,
      // Asegurarse de que el userId se obtiene de la URL
      userId: this.route.snapshot.queryParams['userId']
    };

    this.paymentService.processPayment(paymentData).subscribe({
      next: (result: any) => {
        this.spinner.hide();
        this.paymentResult = result;

        if (result.success) {
          // alert('¡Pago procesado con éxito! Serás redirigido a tu dashboard.');
          this.success('¡Pago procesado con éxito! Serás redirigido a tu dashboard.');
          this.router.navigate(['/dashboard']);
        } else {
          // Usar el manejador de errores para mensajes amigables
          const friendlyMessage = this.getFriendlyErrorMessage(result.message);
          // alert(`Error en el pago: ${friendlyMessage}`);
          this.info(`Error en el pago: ${friendlyMessage}`, '', 'Aceptar', true);
        }
      },
      error: (err: any) => {
        this.spinner.hide();
        console.error('Error al procesar el pago:', err);

        // Extraer el mensaje de error si está disponible
        const errorMessage = err.error?.message || err.message || 'Error desconocido';
        const friendlyMessage = this.getFriendlyErrorMessage(errorMessage);

        this.error(`Error: ${friendlyMessage}`);
      }
    });
  }

  validateForm(): boolean {
    // Validación básica del formulario
    return this.cardName.trim() !== '' &&
      this.cardNumber.trim() !== '' &&
      this.expiryDate.trim() !== '' &&
      this.cvv.trim() !== '';
  }

  handleCancel(): void {
    // Redirigimos a la selección de planes dentro del dashboard
    this.router.navigate(['/dashboard/plans']);
  }

  /**
   * Carga el formulario de pago de Alignet
   */
  async cargarFormularioAlignet(): Promise<void> {
    if (!this.alignetForm || !this.alignetLoading) {
      console.error('Elementos del formulario de Alignet no encontrados');
      return;
    }

    try {
      // Datos del cliente (puedes obtenerlos del usuario autenticado)
      const customerData = {
        firstName: this.cardName || 'Cliente',
        lastName: 'Usuario',
        email: 'cliente@phoenix.com', // Obtener del usuario autenticado
        phone: '999999999',
        address: 'Av. Principal 123',
        city: 'Lima',
        state: 'Lima',
        country: 'Peru'
      };

      // Preparar información del pago
      const paymentInfo = {
        userId: this.route.snapshot.queryParams['userId'],
        planId: this.planId,
        planName: this.planName,
        frequency: this.frequency
      };

      await this.alignetPaymentService.cargarFormulario(
        this.alignetForm.nativeElement,
        this.alignetLoading.nativeElement,
        this.amount,
        customerData,
        paymentInfo
      );
    } catch (error) {
      console.error('Error al cargar formulario de Alignet:', error);
      this.info('Error al cargar el formulario de pago. Por favor, intenta con otro método.', '', 'Aceptar', true);
    }
  }

  private getFriendlyErrorMessage(error: string): string {
    const lowerCaseError = error.toLowerCase();

    if (lowerCaseError.includes('userid is required')) {
      return 'No se pudo verificar tu identidad. Por favor, intenta iniciar sesión de nuevo.';
    } else if (lowerCaseError.includes('invalid card number')) {
      return 'El número de tarjeta no es válido. Por favor, revísalo.';
    } else if (lowerCaseError.includes('card has expired')) {
      return 'La tarjeta ha expirado. Por favor, usa una tarjeta diferente.';
    } else if (lowerCaseError.includes('insufficient funds')) {
      return 'Fondos insuficientes. Por favor, prueba con otra tarjeta o método de pago.';
    } else if (lowerCaseError.includes('payment declined')) {
      return 'El pago fue rechazado por el banco. Por favor, contacta a tu banco o usa otra tarjeta.';
    }

    // Mensaje por defecto para otros errores
    return 'Ocurrió un problema al procesar tu pago. Por favor, inténtalo de nuevo más tarde.';
  }
} 